import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, TextField, Button, Alert, Paper, MenuItem } from '@mui/material';
import { motion } from 'framer-motion';
import { redeemCoupon, RedeemCouponResponse, fetchActiveCouponBatch } from '../services/couponService';
import { NEIGHBORHOODS } from '../domain/neighborhoods';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';

const CouponRedeem: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [batchId, setBatchId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [bairro, setBairro] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [coupon, setCoupon] = useState<RedeemCouponResponse | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadBatch = async () => {
      if (!company?.cnpj) return;
      
      try {
        setLoading(true);
        const batch = await fetchActiveCouponBatch(company.cnpj);
        if (!isMounted) return;

        if (batch) {
          setBatchId(batch.batchId);
        } else {
          setError('Nenhuma campanha ativa de cupons encontrada no momento.');
        }
      } catch (err: any) {
        let message = err?.response?.data?.error || err.message || 'Erro ao buscar campanha de cupons.';

        const rawMessage = (err?.response?.data?.error || err?.message || '').toString();

        if (rawMessage.includes('No active coupon campaign found')) {
          message = 'Não há campanha de resgate ativa no momento.';
        }

        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBatch();

    return () => {
      isMounted = false;
    };
  }, [company?.cnpj]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setCoupon(null);

    if (!batchId) {
      setError('Campanha inválida. Entre em contato com a loja.');
      return;
    }

    if (!name.trim() || !phone.trim() || !email.trim() || !bairro.trim()) {
      setError('Preencha todos os campos para resgatar seu cupom.');
      return;
    }

    if (!company?.cnpj) {
      setError('Erro ao identificar empresa. Recarregue a página.');
      return;
    }

    setLoading(true);
    try {
      const result = await redeemCoupon(company.cnpj, {
        batchId,
        name: name.trim(),
        phone: phone.replace(/\D/g, ''),
        email: email.trim(),
        bairro: bairro.trim(),
      });
      setCoupon(result);
      setSuccess('Cupom resgatado com sucesso! Confira seu e-mail e os detalhes abaixo.');
    } catch (err: any) {
      let message = err?.response?.data?.error || err.message || 'Erro ao resgatar cupom.';

      const rawMessage = (err?.response?.data?.error || err?.message || '').toString();

      // Tratamentos específicos para erros técnicos (SMTP, credenciais, etc.)
      if (rawMessage.includes('Missing credentials for "PLAIN"') || rawMessage.toLowerCase().includes('smtp')) {
        message = 'Neste momento não foi possível resgatar seu cupom. Tente mais tarde!';
      } else if (rawMessage.includes('Phone already has a coupon for this campaign')) {
        message = 'Número de telefone e e-mail já vinculados a um cupom para essa campanha.';
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
      >
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Resgate seu cupom
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Preencha seus dados para participar da campanha e receber um cupom de desconto exclusivo.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Telefone (WhatsApp)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Bairro"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                fullWidth
                required
                select
              >
                <MenuItem value="" disabled>
                  Selecione...
                </MenuItem>
                {NEIGHBORHOODS.map((neighborhood) => (
                  <MenuItem key={neighborhood} value={neighborhood}>
                    {neighborhood}
                  </MenuItem>
                ))}
              </TextField>

              <Box mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Resgatar cupom'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>

        {coupon && (
          <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Cupom resgatado
            </Typography>
            <Typography variant="body1">
              Código do cupom: <strong>{coupon.code}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Valor de desconto: R$ {coupon.value.toFixed(2).replace('.', ',')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pedido mínimo: R$ {coupon.minOrderValue.toFixed(2).replace('.', ',')}
            </Typography>
            {coupon.expiresAt && (
              <Typography variant="body2" color="text.secondary">
                Válido até: {new Date(coupon.expiresAt).toLocaleString('pt-BR')}
              </Typography>
            )}
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
              >
                Ir para a página inicial
              </Button>
            </Box>
          </Paper>
        )}
      </motion.div>
    </Container>
  );
};

export default CouponRedeem;
